import React, { useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Stack,
  Chip,
  TextField,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import Sidebar from "../Sidebar/Sidebar";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import InfoIcon from "@mui/icons-material/Info";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";

const rows = [
  {
    recording: "Team Standup - May 17",
    person: "Carol White",
    business: "Tech Corp",
    location: "Remote",
    date: "5/17/2024",
    uploadedBy: "Mike Brown",
    aiStatus: "Pending",
    surveyStatus: "Not Started",
  },
  {
    recording: "Product Strategy Workshop",
    person: "Henry Zhang",
    business: "Tech Corp",
    location: "Denver, CO",
    date: "5/13/2024",
    uploadedBy: "Emma Davis",
    aiStatus: "Pending",
    surveyStatus: "In Progress",
  },
];

export default function Recording() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [mobileOpen, setMobileOpen] =useState(false);

  return (
    <Box
      sx={{
        display: "flex",
        background: "#F8FAFC",
        minHeight: "100vh",
        overflow: "hidden",
      }}
    >
      <Sidebar
        mobileOpen={mobileOpen}
        setMobileOpen={setMobileOpen}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: "100%",
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            p: {
              xs: 2,
              sm: 3,
              md: 4,
            },
          }}
        >
          {isMobile && (
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              mb={3}
            >
              <Box
                display="flex"
                alignItems="center"
              >
                <IconButton
                  onClick={() =>
                    setMobileOpen(true)
                  }
                >
                  <MenuRoundedIcon />
                </IconButton>

                <Typography
                  variant="h6"
                  fontWeight={800}
                >
                  Recordings
                </Typography>
              </Box>

              <Button
                variant="contained"
                size="small"
                startIcon={<AddRoundedIcon />}
                onClick={() => navigate("/recordings/add")}
                sx={{
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 600,
                  boxShadow: "none",
                }}
              >
                Add
              </Button>
            </Stack>
          )}

          {!isMobile && (
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={4}
            >
              <Typography
                variant="h4"
                fontWeight={800}
              >
                Recordings
              </Typography>

              <Button
                variant="contained"
                startIcon={<AddRoundedIcon />}
                onClick={() => navigate("/recordings/add")}
                sx={{
                  borderRadius: "12px",
                  px: 3,
                  py: 1.2,
                  textTransform: "none",
                  fontWeight: 600,
                  boxShadow: "none",
                  background:
                    "linear-gradient(135deg,#2563EB 0%,#06B6D4 100%)",

                  "&:hover": {
                    background:
                      "linear-gradient(135deg,#1D4ED8 0%,#0891B2 100%)",
                    boxShadow:
                      "0 8px 20px rgba(37,99,235,0.25)",
                  },
                }}
              >
                Add Recording
              </Button>
            </Stack>
          )}

          {/* FILTERS */}
          <Paper
            sx={{
              mt: 4,
              p: {
                xs: 2,
                sm: 3,
              },
              borderRadius: 4,
              boxShadow:
                "0 8px 24px rgba(0,0,0,0.04)",
            }}
          >
            <Stack
              direction={{
                xs: "column",
                sm: "row",
              }}
              justifyContent="space-between"
              alignItems={{
                xs: "stretch",
                sm: "center",
              }}
              spacing={2}
              mb={3}
            >
              <Typography
                variant="h6"
                fontWeight={700}
              >
                Filters
              </Typography>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12} lg={4}>
                <TextField
                  fullWidth
                  placeholder="Search by name, person, or business"
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6} lg={2}>
                <TextField
                  select
                  fullWidth
                  label="Period"
                  defaultValue="Daily"
                  size="small"
                >
                  <MenuItem value="Daily">
                    Daily
                  </MenuItem>

                  <MenuItem value="Weekly">
                    Weekly
                  </MenuItem>

                  <MenuItem value="Monthly">
                    Monthly
                  </MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6} lg={2}>
                <TextField
                  select
                  fullWidth
                  label="AI Status"
                  defaultValue="Pending"
                  size="small"
                >
                  <MenuItem value="Pending">
                    Pending
                  </MenuItem>

                  <MenuItem value="Completed">
                    Completed
                  </MenuItem>

                  <MenuItem value="In Progress">
                    In Progress
                  </MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6} lg={2}>
                <TextField
                  fullWidth
                  type="date"
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6} lg={2}>
                <Stack
                  direction="row"
                  spacing={1}
                >
                  <Button
                    fullWidth
                    variant="contained"
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      boxShadow: "none",
                    }}
                  >
                    Apply
                  </Button>

                  <Button
                    fullWidth
                    variant="outlined"
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                    }}
                  >
                    Clear
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          </Paper>

          {/* TABLE */}
          {isMobile ? (
            <Stack spacing={2} mt={4}>
              {rows.map((row, index) => (
                <Paper
                  key={index}
                  sx={{
                    p: 2,
                    borderRadius: 3,
                    boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
                  }}
                >
                  {/* Header */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      mb: 2,
                    }}
                  >
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      color="primary"
                      sx={{
                        flex: 1,
                        pr: 1,
                      }}
                    >
                      {row.recording}
                    </Typography>

                    <Chip
                      label={row.aiStatus}
                      size="small"
                      sx={{
                        backgroundColor:
                          row.aiStatus === "Completed"
                            ? "#DCFCE7"
                            : row.aiStatus === "In Progress"
                            ? "#DBEAFE"
                            : "#FEF3C7",
                        color:
                          row.aiStatus === "Completed"
                            ? "#166534"
                            : row.aiStatus === "In Progress"
                            ? "#1D4ED8"
                            : "#92400E",
                      }}
                    />
                  </Box>

                  {/* Details */}
                  <Grid container spacing={2}>
                    <Grid item xs={6}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        Person
                      </Typography>

                      <Typography
                        variant="body2"
                        fontWeight={500}
                      >
                        {row.person}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        Business
                      </Typography>

                      <Typography
                        variant="body2"
                        fontWeight={500}
                      >
                        {row.business}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        Location
                      </Typography>

                      <Typography
                        variant="body2"
                        fontWeight={500}
                      >
                        {row.location}
                      </Typography>
                    </Grid>

                    <Grid item xs={6}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        Date
                      </Typography>

                      <Typography
                        variant="body2"
                        fontWeight={500}
                      >
                        {row.date}
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        Uploaded By
                      </Typography>

                      <Typography
                        variant="body2"
                        fontWeight={500}
                      >
                        {row.uploadedBy}
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                      >
                        Survey Status
                      </Typography>

                      <Box mt={0.5}>
                        <Chip
                          label={row.surveyStatus}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    </Grid>
                  </Grid>

                  {/* Actions */}
                  <Box
                    sx={{
                      mt: 2,
                      pt: 2,
                      borderTop:
                        "1px solid rgba(0,0,0,0.08)",
                    }}
                  >
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                    >
                      <Stack
                        direction="row"
                        spacing={1}
                      >
                        <IconButton
                          size="small"
                          color="primary"
                        >
                          <PlayArrowIcon />
                        </IconButton>

                        <IconButton
                          size="small"
                          color="info"
                        >
                          <InfoIcon />
                        </IconButton>
                      </Stack>

                      <Stack
                        direction="row"
                        spacing={1}
                      >
                        <IconButton
                          size="small"
                          color="success"
                        >
                          <VisibilityIcon />
                        </IconButton>

                        <IconButton
                          size="small"
                          color="primary"
                        >
                          <EditIcon />
                        </IconButton>

                        <IconButton
                          size="small"
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Stack>
                    </Stack>
                  </Box>
                </Paper>
              ))}
            </Stack>
          ) : (
            <Paper
              sx={{
                mt: 4,
                borderRadius: 4,
                overflow: "hidden",
                boxShadow:
                  "0 8px 24px rgba(0,0,0,0.04)",
              }}
            >
              <TableContainer
                sx={{
                  overflowX: "auto",
                }}
              >
                <Table
                  sx={{
                    minWidth: 1200,
                  }}
                >
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        Recording Name
                      </TableCell>

                      <TableCell>
                        Person Name
                      </TableCell>

                      <TableCell>
                        Business
                      </TableCell>

                      <TableCell>
                        Location
                      </TableCell>

                      <TableCell>Date</TableCell>

                      <TableCell>
                        Uploaded By
                      </TableCell>

                      <TableCell>
                        AI Status
                      </TableCell>

                      <TableCell>
                        Survey Status
                      </TableCell>

                      <TableCell>
                        Audio
                      </TableCell>

                      <TableCell>Text</TableCell>

                      <TableCell>
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {rows.map(
                      (row, index) => (
                        <TableRow key={index}>
                          <TableCell
                            sx={{
                              color: "#2563EB",
                              fontWeight: 600,
                              whiteSpace:
                                "nowrap",
                            }}
                          >
                            {row.recording}
                          </TableCell>

                          <TableCell>
                            {row.person}
                          </TableCell>

                          <TableCell>
                            {row.business}
                          </TableCell>

                          <TableCell>
                            {row.location}
                          </TableCell>

                          <TableCell>
                            {row.date}
                          </TableCell>

                          <TableCell>
                            {row.uploadedBy}
                          </TableCell>

                          <TableCell>
                            <Chip
                              label={
                                row.aiStatus
                              }
                              size="small"
                              sx={{
                                backgroundColor:
                                  "#FEF3C7",
                                color:
                                  "#92400E",
                              }}
                            />
                          </TableCell>

                          <TableCell>
                            <Chip
                              label={
                                row.surveyStatus
                              }
                              size="small"
                            />
                          </TableCell>

                          <TableCell>
                            <IconButton color="primary">
                              <PlayArrowIcon />
                            </IconButton>
                          </TableCell>

                          <TableCell>
                            <IconButton color="info">
                              <InfoIcon />
                            </IconButton>
                          </TableCell>

                          <TableCell>
                            <Stack
                              direction="row"
                              spacing={1}
                            >
                              <IconButton color="success">
                                <VisibilityIcon />
                              </IconButton>

                              <IconButton color="primary">
                                <EditIcon />
                              </IconButton>

                              <IconButton color="error">
                                <DeleteIcon />
                              </IconButton>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      )
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}
        </Box>
      </Box>
    </Box>
  );
}